/* Reusable Carousel Component */

class Carousel {
  constructor(wrapper, options = {}) {
    this.wrapper = wrapper;
    this.track = wrapper.querySelector('[data-carousel-track]');
    this.prevButton = wrapper.querySelector('.carousel__button--prev');
    this.nextButton = wrapper.querySelector('.carousel__button--next');
    this.indicatorsContainer = wrapper.querySelector('.carousel__indicators');

    this.options = {
      itemsPerView: options.itemsPerView || this.getDefaultItemsPerView(),
      gap: options.gap || this.getDefaultGap(),
      pageBasedIndicators: options.pageBasedIndicators !== false,
      ...options,
    };

    this.currentPage = 0;

    if (!this.track || !this.prevButton || !this.nextButton) {
      console.warn('Carousel: Missing required elements');
      return;
    }

    this.items = this.track.querySelectorAll('.carousel__item');
    if (this.items.length === 0) {
      console.warn('Carousel: No items found');
      return;
    }

    this.init();
  }

  getDefaultItemsPerView() {
    const isTestimonial = this.wrapper.closest('.testimonial-carousel');
    const isFarm = this.wrapper.closest('.farm-carousel');
    const isVideoCarousel = this.wrapper.closest('.carousel-video');
    const isBlogPosts = this.wrapper.closest('.carousel-featured-blog-posts');
    const isRecommendedRecipes = this.wrapper.closest('.carousel-recommended-recipes');

    // These carousels have fixed/special layouts — keep breakpoint-based for them
    if (isVideoCarousel || isFarm || isTestimonial || isBlogPosts || isRecommendedRecipes) {
      return () => {
        const width = window.innerWidth;
        if (isVideoCarousel) return width < 750 ? 1 : 3;
        if (isFarm) return 1;
        if (isTestimonial) return width < 750 ? 1 : width < 1200 ? 2 : 3;
        if (isBlogPosts || isRecommendedRecipes) {
          if (width < 550) return 1;
          if (width < 750) return 2;
          if (width < 990) return 2;
          if (width < 1200) return 3;
          return 4;
        }
      };
    }

    // For all other carousels, measure from the DOM so itemsPerView
    // always matches whatever the CSS is actually rendering
    return () => {
      const firstItem = this.track?.querySelector('.carousel__item');
      if (firstItem && firstItem.offsetWidth > 0) {
        const gap =
          parseFloat(getComputedStyle(this.track).columnGap) || this.getDefaultGap();
        return Math.max(1, Math.round((this.track.clientWidth + gap) / (firstItem.offsetWidth + gap)));
      }
      const width = window.innerWidth;
      if (width < 750) return 2;
      if (width < 1200) return 4;
      return 5;
    };
  }

  getDefaultGap() {
    return window.innerWidth >= 750 ? 30 : 20;
  }

  getItemWidth() {
    return this.items[0].offsetWidth;
  }

  getGap() {
    if (typeof this.options.gap === 'function') {
      return this.options.gap();
    }
    return this.options.gap;
  }

  getItemsPerView() {
    if (typeof this.options.itemsPerView === 'function') {
      return this.options.itemsPerView();
    }
    return this.options.itemsPerView;
  }

  getTotalPages() {
    const visibleItems = this.getVisibleItemsCount();
    return Math.ceil(this.items.length / visibleItems);
  }

  getCurrentPageInfo() {
    const visibleItems = this.getVisibleItemsCount();
    const startItem = this.currentPage * visibleItems + 1;
    const endItem = Math.min((this.currentPage + 1) * visibleItems, this.items.length);
    const totalItems = this.items.length;

    return { startItem, endItem, totalItems };
  }

  createPageIndicators() {
    if (!this.indicatorsContainer || !this.options.pageBasedIndicators) return;

    // Clear existing indicators
    this.indicatorsContainer.innerHTML = '';

    const totalPages = this.getTotalPages();

    for (let i = 0; i < totalPages; i++) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'carousel__indicator';
      button.setAttribute('aria-label', `Go to page ${i + 1} of ${totalPages}`);
      button.setAttribute('aria-current', i === 0 ? 'page' : 'false');
      button.dataset.page = i;

      if (i === 0) {
        button.classList.add('is-active');
      }

      button.addEventListener('click', () => {
        this.goToPage(i);
      });

      this.indicatorsContainer.appendChild(button);
    }

    this.indicators = this.indicatorsContainer.querySelectorAll('.carousel__indicator');
  }

  createAriaLiveRegion() {
    // Create for screen readers
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only';
    liveRegion.id = `carousel-live-${Math.random().toString(36).substr(2, 9)}`;

    const style = document.createElement('style');
    style.textContent = `
      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border-width: 0;
      }
    `;
    document.head.appendChild(style);

    this.wrapper.appendChild(liveRegion);
    this.liveRegion = liveRegion;
  }

  announcePosition() {
    if (!this.liveRegion) return;

    const { startItem, endItem, totalItems } = this.getCurrentPageInfo();
    const message = `Showing items ${startItem} through ${endItem} of ${totalItems}`;

    this.liveRegion.textContent = message;
  }

  updateButtons() {
    const totalPages = this.getTotalPages();

    // Enable infinite looping - buttons are never disabled
    this.prevButton.disabled = false;
    this.nextButton.disabled = false;

    // Check if track is actually scrollable (has overflow)
    const isScrollable = this.track.scrollWidth > this.track.clientWidth;

    // Hide buttons only if all items fit on screen AND track is not scrollable
    if (totalPages <= 1 && !isScrollable) {
      this.prevButton.style.display = 'none';
      this.nextButton.style.display = 'none';
      if (this.indicatorsContainer) {
        this.indicatorsContainer.style.display = 'none';
      }
      this.wrapper.classList.add('carousel__wrapper--no-navigation');
    } else {
      this.prevButton.style.display = '';
      this.nextButton.style.display = '';
      if (this.indicatorsContainer) {
        this.indicatorsContainer.style.display = '';
      }
      this.wrapper.classList.remove('carousel__wrapper--no-navigation');
    }
  }

  updateSlideVisibility() {
    const itemsPerView = this.getItemsPerView();
    const start = this.currentPage * itemsPerView;
    const end = start + itemsPerView;

    this.items.forEach((item, index) => {
      if (index >= start && index < end) {
        item.removeAttribute('inert');
      } else {
        item.setAttribute('inert', '');
      }
    });
  }

  updateIndicators() {
    if (!this.indicators || this.indicators.length === 0) return;

    this.indicators.forEach((indicator, index) => {
      const isActive = index === this.currentPage;
      indicator.classList.toggle('is-active', isActive);
      indicator.setAttribute('aria-current', isActive ? 'page' : 'false');
    });
  }

  getVisibleItemsCount() {
    // Calculate how many items actually fit in the visible viewport
    const trackWidth = this.track.offsetWidth;
    const itemWidth = this.getItemWidth();
    const gap = this.getGap();

    // Calculate visible items based on available space
    return Math.floor((trackWidth + gap) / (itemWidth + gap));
  }

  goToPage(pageIndex) {
    const totalPages = this.getTotalPages();

    // Clamp page index
    pageIndex = Math.max(0, Math.min(pageIndex, totalPages - 1));

    // Use visible items count for scrolling (full viewport width)
    const visibleItems = this.getVisibleItemsCount();
    const targetItemIndex = pageIndex * visibleItems;
    const targetItem = this.items[targetItemIndex];

    if (targetItem) {
      const scrollAmount = targetItem.offsetLeft - this.track.offsetLeft;

      this.track.scrollTo({
        left: scrollAmount,
        behavior: 'smooth',
      });
    }

    this.currentPage = pageIndex;
    this.updateButtons();
    this.updateIndicators();
    this.updateSlideVisibility();
    this.announcePosition();
  }

  goToPrevPage() {
    const totalPages = this.getTotalPages();
    // Loop to last page if at first page
    if (this.currentPage <= 0) {
      this.goToPage(totalPages - 1);
    } else {
      this.goToPage(this.currentPage - 1);
    }
  }

  goToNextPage() {
    const totalPages = this.getTotalPages();
    // Loop to first page if at last page
    if (this.currentPage >= totalPages - 1) {
      this.goToPage(0);
    } else {
      this.goToPage(this.currentPage + 1);
    }
  }

  handleResize() {
    // Recalculate pages on resize
    const oldTotalPages = this.getTotalPages();

    // Recreate indicators if page count changed
    if (this.options.pageBasedIndicators) {
      const newTotalPages = this.getTotalPages();
      if (newTotalPages !== oldTotalPages) {
        this.createPageIndicators();
      }
    }

    // Adjust current page if needed
    const totalPages = this.getTotalPages();
    if (this.currentPage >= totalPages) {
      this.currentPage = totalPages - 1;
    }

    this.updateButtons();
    this.goToPage(this.currentPage); // Recalculate scroll position
  }

  attachEventListeners() {
    // Navigation buttons
    this.prevButton.addEventListener('click', () => {
      this.goToPrevPage();
    });

    this.nextButton.addEventListener('click', () => {
      this.goToNextPage();
    });

    // Handle window resize with debounce
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => this.handleResize(), 250);
    });

    // Add swipe support
    this.initSwipeSupport();
  }

  initSwipeSupport() {
    if (typeof SwipeHandler === 'undefined') return;

    this.swipeHandler = new SwipeHandler(this.track, {
      onSwipeLeft: () => this.goToNextPage(),
      onSwipeRight: () => this.goToPrevPage(),
      threshold: 50,
      restraint: 100,
      allowedTime: 500,
    });
  }

  init() {
    this.createAriaLiveRegion();

    if (this.options.pageBasedIndicators) {
      this.createPageIndicators();
    } else {
      this.indicators = this.indicatorsContainer?.querySelectorAll('.carousel__indicator') || [];
    }

    this.attachEventListeners();
    this.updateButtons();
    this.updateIndicators();
    this.updateSlideVisibility();
    this.announcePosition();
  }

  destroy() {
    if (this.liveRegion) {
      this.liveRegion.remove();
    }
    if (this.swipeHandler) {
      this.swipeHandler.destroy();
    }
  }
}

// Auto-initialize carousels on page load
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('.carousel__wrapper').forEach((wrapper) => {
    if (!wrapper.hasAttribute('data-manual-init')) {
      new Carousel(wrapper, { pageBasedIndicators: true });
    }
  });
});

if (typeof module !== 'undefined' && module.exports) {
  module.exports = Carousel;
}
